import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import React, { useMemo, useState } from 'react';
import formStyles from 'legacy/common/components/form/Form.module.scss';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { useTextAllGuests } from 'legacy/events/hooks';
import { EventTierForOrganizer } from 'legacy/events/types';
import { useGetEventProducts } from 'api/queries/events/products';
import { showModal } from 'legacy/actions/modalActions';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import classnames from 'classnames';
import { MdChevronRight } from 'react-icons/md';
import RecipientPicker, { RecipientOption } from './RecipientPicker';

const MAX_MESSAGE_LENGTH = 240;

const TextAllGuestsSchema = Yup.object().shape({
  message: Yup.string()
    .required('Message is required')
    .max(
      MAX_MESSAGE_LENGTH,
      `Message must be ${MAX_MESSAGE_LENGTH} characters or less`
    )
});

type TextAllGuestsProps = {
  eventId: string;
  tiers?: EventTierForOrganizer[];
  totalGuests?: number;
  onClose: () => void;
  visible: boolean;
};

const ALL_TIERS_OPTION: RecipientOption = {
  id: null,
  name: 'All Tiers',
  guestCount: 0
};

const TextAllGuestsModal: React.FC<TextAllGuestsProps> = ({
  eventId,
  tiers = [],
  totalGuests = 0,
  onClose,
  visible
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] =
    useState<RecipientOption | null>(null);
  const dispatch = useDispatch();
  const textAllGuests = useTextAllGuests();

  const { data: productsData } = useGetEventProducts(
    { eventId },
    { enabled: visible }
  );

  const recipientOptions = useMemo<RecipientOption[]>(() => {
    const allTiers: RecipientOption = {
      ...ALL_TIERS_OPTION,
      guestCount: totalGuests
    };

    const tierOptions: RecipientOption[] = tiers.map((tier) => ({
      id: tier._id,
      name: tier.name,
      guestCount: tier.totalGuests ?? 0
    }));

    const bundleOptions: RecipientOption[] = (productsData?.items ?? [])
      .filter(
        (product) =>
          product.type === 'bundle' && product.isActive && product.isVisible
      )
      .map((product) => ({
        id: product.id,
        name: product.name,
        guestCount: product.purchases,
        isBundle: true
      }));

    return [allTiers, ...tierOptions, ...bundleOptions];
  }, [tiers, totalGuests, productsData]);

  const activeRecipient = selectedRecipient ?? recipientOptions[0];

  return (
    <>
      <Modal
        visible={visible}
        type={ModalType.VERY_SMALL}
        onClose={onClose}
        minHeight="500px"
      >
        <div className="h-full flex flex-col py-12 px-10">
          <h1 className="shrink-0 text-2xl text-gray-500 font-normal">
            Text all guests
          </h1>
          <p className="font-bold mt-8">
            {activeRecipient?.id
              ? `You're about to send an SMS to ${activeRecipient.name} guests!`
              : "You're about to send an SMS to all your guests!"}
          </p>
          <p className="mt-4">
            Have you considered using the event post feed for a more interactive
            announcement?
          </p>

          <div className={formStyles.group}>
            <div className={formStyles.label}>Recipients</div>
            <button
              type="button"
              className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-2xl hover:bg-gray-50 transition-colors"
              onClick={() => setIsPickerOpen(true)}
              disabled={isComplete}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold text-sm">
                  {activeRecipient?.name}
                </span>
                <span className="text-xs text-gray-400">
                  {activeRecipient?.guestCount}{' '}
                  {activeRecipient?.guestCount === 1 ? 'guest' : 'guests'}
                </span>
              </div>
              <MdChevronRight size={20} className="text-gray-400" />
            </button>
          </div>

          <Formik
            validationSchema={TextAllGuestsSchema}
            initialValues={{
              message: ''
            }}
            onSubmit={({ message }) => {
              setIsLoading(true);
              textAllGuests.mutate(
                {
                  eventId,
                  message,
                  ...(activeRecipient?.id && { tierId: activeRecipient.id })
                },
                {
                  onSuccess: () => {
                    setIsLoading(false);
                    setIsComplete(true);
                  },
                  onError: (error) => {
                    dispatch(
                      showModal('ALERT', {
                        message: error?.message
                      })
                    );
                    setIsLoading(false);
                  }
                }
              );
            }}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form className={formStyles.container}>
                <div className={formStyles.group}>
                  <div className={formStyles.label}>Message</div>
                  <Field
                    id="message"
                    name="message"
                    as="textarea"
                    placeholder="Enter your message"
                    className={classnames(
                      formStyles.textarea,
                      'h-56 resize-none',
                      {
                        [formStyles.errorField]:
                          errors.message && touched.message
                      }
                    )}
                    disabled={isComplete}
                    maxLength={MAX_MESSAGE_LENGTH}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      const message = e.target.value;
                      setFieldValue('message', message);
                    }}
                    value={values.message}
                  />
                  <div className="text-gray-500 text-sm text-right">
                    {MAX_MESSAGE_LENGTH - values.message.length} characters
                    remaining
                  </div>
                  {errors.message && touched.message && (
                    <div className={formStyles.error}>{errors.message}</div>
                  )}
                </div>

                <div className={formStyles.button}>
                  <Button
                    isSubmit
                    loading={isLoading}
                    disabled={isComplete}
                    type={ButtonType.Primary}
                  >
                    {isComplete ? 'SMS Sent' : 'Send SMS'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Modal>

      <RecipientPicker
        visible={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        options={recipientOptions}
        selectedId={activeRecipient?.id ?? null}
        onSelect={setSelectedRecipient}
      />
    </>
  );
};

export default TextAllGuestsModal;
